import React, { useState, useEffect } from 'react';
import { webhookAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './Webhooks.css';

const EVENTS = ['plan.upgraded', 'plan.downgraded', 'room.checked_in', 'room.checked_out', 'user.expired', 'addon.purchased', 'membership.upgraded'];

const DEFAULT_FORM = { name: '', url: '', events: [], isActive: true };

const Webhooks = () => {
    const { selectedBranch } = useBranchStore();
    const [webhooks, setWebhooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_FORM);

    useEffect(() => { fetchAll(); }, [selectedBranch]);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const branchId = selectedBranch?._id;
            const res = await webhookAPI.getAll(branchId ? { branchId } : undefined);
            setWebhooks(res.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch webhooks');
        } finally {
            setLoading(false);
        }
    };

    const handleNew = () => { setEditing(null); setFormData(DEFAULT_FORM); setShowModal(true); };

    const handleEdit = (wh) => {
        setEditing(wh);
        setFormData({ name: wh.name, url: wh.url, events: wh.events || [], isActive: wh.isActive !== false });
        setShowModal(true);
    };

    const toggleEvent = (evt) => {
        setFormData(prev => ({
            ...prev,
            events: prev.events.includes(evt) ? prev.events.filter(e => e !== evt) : [...prev.events, evt],
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !formData.url.trim()) return toast.warning('Name and URL are required');
        if (formData.events.length === 0) return toast.warning('Select at least one event');
        try {
            if (editing) {
                await webhookAPI.update(editing._id, formData);
                toast.success('Webhook updated');
            } else {
                await webhookAPI.create(formData);
                toast.success('Webhook created');
            }
            setShowModal(false);
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDelete = async (wh) => {
        if (!window.confirm(`Delete webhook "${wh.name}"?`)) return;
        try {
            await webhookAPI.delete(wh._id);
            toast.success('Webhook deleted');
            fetchAll();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const handleTest = async (wh) => {
        try {
            await webhookAPI.test(wh._id);
            toast.success('Test event sent');
        } catch (error) {
            toast.error('Failed to send test event');
        }
    };

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern"><i className="fas fa-satellite-dish"></i> Webhooks</h1>
                        <p className="page-subtitle-modern">Notify an external system when plan upgrades, check-ins/outs, expiry, or add-on purchases happen — signed with HMAC-SHA256</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary-modern" onClick={handleNew}><i className="fas fa-plus-circle"></i> Add Webhook</button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading webhooks...</p></div>
            ) : webhooks.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-satellite-dish"></i></div>
                    <h3>No Webhooks Yet</h3>
                    <p>Subscribe an external URL to events like plan upgrades or room check-outs.</p>
                </div>
            ) : (
                <div className="resource-grid-modern">
                    {webhooks.map(wh => (
                        <div key={wh._id} className="resource-card-modern">
                            <div className="card-header">
                                <div className="resource-avatar" style={{ background: '#0ea5e915', color: '#0ea5e9' }}><i className="fas fa-satellite-dish"></i></div>
                                <div className="resource-title">
                                    <h3>{wh.name}</h3>
                                    <span className="resource-subtitle webhook-url">{wh.url}</span>
                                </div>
                                <div className="card-actions">
                                    <button className="action-btn-icon" onClick={() => handleTest(wh)} title="Send test event"><i className="fas fa-paper-plane"></i></button>
                                    <button className="action-btn-icon edit" onClick={() => handleEdit(wh)}><i className="fas fa-edit"></i></button>
                                    <button className="action-btn-icon delete" onClick={() => handleDelete(wh)}><i className="fas fa-trash"></i></button>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="status-badges">
                                    <span className={`status-badge ${wh.isActive ? 'active' : 'inactive'}`}><span className="badge-dot"></span>{wh.isActive ? 'Active' : 'Inactive'}</span>
                                    {wh.lastStatus && <span className={`status-badge ${wh.lastStatus === 'success' ? 'active' : 'inactive'}`}>Last: {wh.lastStatus}</span>}
                                </div>
                                <div className="webhook-events">
                                    {wh.events.map(e => <span key={e} className="webhook-event-chip">{e}</span>)}
                                </div>
                                <p className="webhook-secret-hint">Secret: <code>{wh.secret?.slice(0, 8)}••••••••</code></p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>{editing ? 'Edit Webhook' : 'Add Webhook'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    <div className="form-group-modern full-width">
                                        <label>Name <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label>URL <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <input type="url" value={formData.url} onChange={e => setFormData({ ...formData, url: e.target.value })} placeholder="https://example.com/webhook" required />
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label>Events</label>
                                        <div className="webhook-event-checkboxes">
                                            {EVENTS.map(evt => (
                                                <label key={evt} className={`chip ${formData.events.includes(evt) ? 'chip-active' : ''}`}>
                                                    <input type="checkbox" checked={formData.events.includes(evt)} onChange={() => toggleEvent(evt)} />
                                                    {evt}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label className="checkbox-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />
                                            <span>Active</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern"><i className="fas fa-check"></i> {editing ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Webhooks;
